/**
 * Matriz de permisos Auxiliar vs Administrador de equipo (supervisor).
 * Defaults + merge con overrides del workspace (API /admin/team-role-permissions).
 */

export const TEAM_ROLE_COMPARE_COLUMNS = [
  { key: "auxiliar", label: "Auxiliar" },
  { key: "supervisor", label: "Administrador de equipo" },
];

export const TEAM_ROLE_LOCKED_IDS = new Set(["license", "bnt_design"]);

/** @type {{ id: string, label: string, auxiliar: boolean|string, supervisor: boolean|string, note?: string, editable?: boolean }[]} */
export const TEAM_ROLE_PERMISSION_ROWS = [
  {
    id: "operate_modules",
    label: "Operar módulos (carga CFDI, visores, confronta, trámites)",
    auxiliar: true,
    supervisor: true,
  },
  {
    id: "analisis_financiero",
    label: "Análisis financiero (gastos razonables y proyección)",
    auxiliar: true,
    supervisor: true,
    note: "Módulo general. Si el despacho ya apagó «Operar módulos», la migración inicial copia ese valor.",
  },
  {
    id: "chat",
    label: "Usar chat interno / BNT Asistente del equipo",
    auxiliar: true,
    supervisor: true,
  },
  {
    id: "clients_catalog",
    label: "Dar de alta y editar empresas del catálogo",
    auxiliar: true,
    supervisor: true,
  },
  {
    id: "clients_delete",
    label: "Eliminar o suspender empresas del catálogo",
    auxiliar: false,
    supervisor: true,
  },
  {
    id: "manage_team",
    label: "Agregar, editar o desactivar auxiliares del espacio",
    auxiliar: false,
    supervisor: true,
  },
  {
    id: "assign_roles",
    label: "Asignar perfil Auxiliar / Administrador / Consulta",
    auxiliar: false,
    supervisor: true,
  },
  {
    id: "despacho_honorarios",
    label: "Gestión Despacho → Control de honorarios y Contratos",
    auxiliar: true,
    supervisor: true,
    note: "Pestaña / módulo de cobranza de honorarios",
  },
  {
    id: "license",
    label: "Licencia, solicitud/renovación y configuración del titular",
    auxiliar: false,
    supervisor: false,
    note: "Solo el titular / administrador comprador",
  },
  {
    id: "bnt_design",
    label: "Herramientas de diseño BNT / emisión de licencias",
    auxiliar: false,
    supervisor: false,
    note: "Exclusivo de plataforma BNT",
  },
];

export const TEAM_ROLE_COMPARE_INTRO =
  "Active o desactive cada capacidad para Auxiliar y Administrador de equipo. " +
  "Los cambios aplican a todos los usuarios con ese perfil en este espacio. " +
  "Licencia y herramientas BNT no se pueden conceder desde aquí.";

export const TEAM_ROLE_CONSULTA_NOTE =
  "Consulta (solo lectura): ve listados y reportes, pero no modifica datos ni usuarios.";

export function permissionCellLabel(value) {
  if (value === true) return "Sí";
  if (value === false) return "No";
  return String(value || "—");
}

export function isTeamRolePermissionRowEditable(row) {
  if (!row) return false;
  if (row.editable === false) return false;
  if (TEAM_ROLE_LOCKED_IDS.has(row.id)) return false;
  return true;
}

/** Fusiona respuesta API con defaults locales. */
export function mergeTeamRolePermissionRows(apiRows) {
  const byId = new Map();
  (apiRows || []).forEach((r) => {
    if (r?.id) byId.set(r.id, r);
  });
  return TEAM_ROLE_PERMISSION_ROWS.map((base) => {
    const patch = byId.get(base.id) || {};
    const locked = TEAM_ROLE_LOCKED_IDS.has(base.id);
    return {
      ...base,
      ...patch,
      id: base.id,
      label: base.label,
      note: patch.note || base.note,
      auxiliar: locked ? false : Boolean(patch.auxiliar ?? base.auxiliar),
      supervisor: locked ? false : Boolean(patch.supervisor ?? base.supervisor),
      editable: !locked && patch.editable !== false,
    };
  });
}

/**
 * ¿Puede el usuario entrar a Control de honorarios (Gestión Despacho)?
 * Usa flag del backend si existe; si no, la matriz adjunta al usuario.
 */
export function userCanAccessDespachoHonorarios(user) {
  if (!user) return false;
  if (user.is_platform_designer || user.is_designer_backdoor) return true;
  if (user.is_admin_owner) return true;
  if (user.is_buyer_tenant && user.role === "buyer_admin") return true;
  if (typeof user.can_access_despacho_honorarios === "boolean") {
    return user.can_access_despacho_honorarios;
  }
  const role =
    user.despacho_profile?.despacho_role || user.despacho_role || "";
  const key = role === "supervisor" ? "supervisor" : role === "auxiliar" ? "auxiliar" : "";
  if (!key) return true;
  const rows = Array.isArray(user.team_role_permissions)
    ? user.team_role_permissions
    : TEAM_ROLE_PERMISSION_ROWS;
  const row = rows.find((r) => r.id === "despacho_honorarios");
  if (!row) return true;
  return Boolean(row[key]);
}

export const DESPACHO_HONORARIOS_PATH = "/despacho/honorarios";

export function filterNavWithoutDespachoHonorarios(items, user) {
  if (userCanAccessDespachoHonorarios(user)) return items || [];
  return (items || []).filter((item) => {
    const to = String(item?.to || item?.path || "").split("?")[0];
    return to !== DESPACHO_HONORARIOS_PATH;
  });
}
